import { useEffect, useState } from "react";
import styles from "./index.module.scss";
import { NextPage } from "next";
import Head from "next/head";
import { useAtomValue, useSetAtom } from "jotai";
import StockShortsDailyInterest from "@/components/stock-shorts-daily-interest/StockShortsDailyInterest";
import StockShortsReports from "@/components/stock-shorts-reports/StockShortsReports";
import Tabs, { TabItem } from "@/components/tabs/Tabs";
import { buildStockOgImage } from "@/lib/stock-og";
import { Stock } from "@/models/stock";
import { stockTradingItemIdState, userState } from "@/lib/store";
import LiveShortInterest from "@/components/stock-shorts-live-short-interest/LiveShortInterest";
import { Box, Typography } from "@mui/material";
import { stockPageGetServerSideProps } from "@/lib/stock-page-ssr";
import { stockCanonicalUrl } from "@/lib/stock-url";
import { latestNewsExtraProps } from "@/controllers/stock-news.server";
import { shortHeadline } from "@/lib/short-key-data";
import { shortAsOfSentence, stockTitle } from "@/lib/stock-seo";

const tabs: TabItem[] = [
    { title: "Short Interest Overview", value: "Overview" },
    { title: "Live Short Interest", value: "Live Short Interest" },
    { title: "Official Reports", value: "Official Reports" },
];

/** The as-of line's height (index.module.scss `.asOf`), which the tab area gives up. */
const AS_OF_LINE_HEIGHT = "24px";

const StockShortsPage: NextPage<{ stock: Stock }> = ({ stock }) => {
    // From the stock payload itself (ortex/ortex-gui#1289): no extra request on the render path.
    const shortData = stock?.shortKeyData;
    const user = useAtomValue(userState);
    const asOfSentence = shortAsOfSentence(shortData);

    const [activeTab, setActiveTab] = useState<string>();

    const renderShortsTab = () => {
        switch (activeTab) {
            case "Overview":
                return <StockShortsDailyInterest stock={stock} />;
            case "Live Short Interest":
                return <LiveShortInterest stock={stock} />;
            case "Official Reports":
                return <StockShortsReports stock={stock} />;
        }
    };

    const setStockTradingItemId = useSetAtom(stockTradingItemIdState);

    useEffect(() => {
        stock ? setStockTradingItemId(stock.tradingitem_id) : setStockTradingItemId(null);
    }, [stock]);

    useEffect(() => {
        if (!activeTab) {
            if (user.settings.module_settings["shortInterest->s/[exchange]/[ticker]/short-interest"]?.activeTab) {
                setActiveTab(user.settings.module_settings["shortInterest->s/[exchange]/[ticker]/short-interest"]?.activeTab);
            } else {
                setActiveTab(tabs[0].value);
            }
        }
    }, [activeTab]);

    useEffect(() => {
        if (user.settings.module_settings["shortInterest->s/[exchange]/[ticker]/short-interest"]?.activeTab) setActiveTab(user.settings.module_settings["shortInterest->s/[exchange]/[ticker]/short-interest"].activeTab);
    }, [user.settings.module_settings["shortInterest->s/[exchange]/[ticker]/short-interest"]?.activeTab]);

    const handleChange = (_: React.MouseEvent<HTMLButtonElement>, newAlignment: "Overview" | "Live Short Interest" | "Official Reports") => {
        if (newAlignment) {
            setActiveTab(newAlignment);
        }
    };

    return (
        <>
            {stock &&
                (() => {
                    const { ogImageUrl, cardTitle } = buildStockOgImage(stock, "Short Interest", shortData);
                    const title = stockTitle(stock, "short-interest");
                    return (
                        <Head>
                            <title>{title}</title>
                            <link rel="canonical" href={stockCanonicalUrl(stock, "short-interest")} />
                            <meta name="keywords" content={`${stock.name}, ${stock.exchangesymbol}:${stock.ticker}, Live Short Interest, Short Squeeze, Shorted, Cost to borrow, Days To Cover, Utilization, intraday`} />
                            <meta
                                name="description"
                                content={`${shortHeadline(stock.ticker, shortData)}${shortData ? " " : ""}${stock.name} (${stock.exchangesymbol}:${stock.ticker}) short interest as % of free float, estimated daily from securities-lending data, with cost to borrow, utilization, days to cover and live intraday estimates.`}
                            />
                            <meta name="twitter:card" content="summary_large_image" />
                            <meta name="twitter:title" content={title} />
                            <meta
                                name="twitter:description"
                                content={`${shortHeadline(stock.ticker, shortData)}${shortData ? " " : ""}${stock.name} (${stock.exchangesymbol}:${stock.ticker}) short interest as % of free float, estimated daily from securities-lending data, with cost to borrow, utilization, days to cover and live intraday estimates.`}
                            />
                            <meta name="twitter:url" content={stockCanonicalUrl(stock, "short-interest")} />
                            <meta name="twitter:image" content={ogImageUrl} />
                            <meta name="twitter:image:alt" content={cardTitle} />
                            <meta property="og:title" content={title} />
                            <meta
                                property="og:description"
                                content={`${shortHeadline(stock.ticker, shortData)}${shortData ? " " : ""}${stock.name} (${stock.exchangesymbol}:${stock.ticker}) short interest as % of free float, estimated daily from securities-lending data, with cost to borrow, utilization, days to cover and live intraday estimates.`}
                            />
                            <meta property="og:url" content={stockCanonicalUrl(stock, "short-interest")} />
                            <meta property="og:image" content={ogImageUrl} key="og:image" />
                            <meta property="og:image:width" content="1200" />
                            <meta property="og:image:height" content="630" />
                            <meta property="og:image:alt" content={cardTitle} />
                        </Head>
                    );
                })()}
            <div className={styles.StockShortsPage}>
                {stock ? (
                    <>
                        {/* Under the H1 (the stock header), in the server's HTML: the numbers the meta description quotes. */}
                        {asOfSentence && (
                            <Typography variant="body2" component="p" className={styles.asOf}>
                                {asOfSentence}
                            </Typography>
                        )}
                        <Box sx={{ height: "46px" }}>
                            <Tabs
                                activeTab={activeTab}
                                alignment="center"
                                handleChange={handleChange}
                                module={"shortInterest"}
                                route={"s/[exchange]/[ticker]/short-interest"}
                                setState={setActiveTab}
                                tabs={stock.shorts_type ? tabs : tabs.slice(0, -1)}
                                setDefault
                            />
                        </Box>
                        <Box sx={{ height: asOfSentence ? `calc(100% - 46px - ${AS_OF_LINE_HEIGHT})` : "calc(100% - 46px)" }}>{renderShortsTab()}</Box>
                    </>
                ) : (
                    <h1>Stock not found.</h1>
                )}
            </div>
        </>
    );
};

export const getServerSideProps = stockPageGetServerSideProps({
    tab: "short-interest",
    // RootLayout renders the key-data block (StockKeyData) and the latest ortex.news headlines below the page.
    extraProps: latestNewsExtraProps,
});

export default StockShortsPage;
